import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { toast } from "sonner";
import type { ResponsiveLayouts, Layout } from "react-grid-layout";
import {
  ShowcaseLayout,
  type LayoutItem,
  TradingViewWidget,
  Orderbook002,
  TradePrice,
  TradeOperation,
  PendingOrders,
  BucketPositions,
  BucketCollections,
  LeverageModal,
  NumberPad,
  generateLayout,
  TickerBar,
  DummyComponent,
} from "../components";
import { useMarkets } from "../hooks/useMarkets";
import { useOnboard } from "../hooks/useOnboard";
import { DEFAULT_BASE_URL, DEFAULT_WS_URL } from "../configs/base";
import { DEFAULT_MNEMONIC } from "../components/smartlayout/constants";
import {
  defaultPredefinedLayouts,
  widthfit,
  rightTrade,
  testTrade,
  triPanel,
} from "../configs/layoutConfig";
import {
  SubscriptionProvider,
  useSubscription,
} from "../context/SubscriptionContext";

interface TestLayoutExchangeProps {}

function TestLayoutExchange(_props: TestLayoutExchangeProps) {
  const [compactType, setCompactType] = useState<
    "vertical" | "horizontal" | null
  >("vertical");
  const [allStatic, setAllStatic] = useState(true);
  const [fullWidth, setFullWidth] = useState(false);
  const [selectedBucketId, setSelectedBucketId] = useState<string>("--");
  const [selectedBucketBalance, setSelectedBucketBalance] = useState<
    number | undefined
  >(undefined);
  const [isLeverageModalOpen, setIsLeverageModalOpen] = useState(false);
  const [tempLeverage, setTempLeverage] = useState(10);
  const [isNumberCalculatorOpen, setIsNumberCalculatorOpen] = useState(false);
  const [numberCalculatorValue, setNumberCalculatorValue] = useState(0);
  const [numberCalculatorMax, setNumberCalculatorMax] = useState(0);
  const [layoutType, setLayoutType] = useState<
    "default" | "widthfit" | "rightTrade" | "testTrade" | "triPanel"
  >("default");
  const showcaseLayoutRef = useRef<ShowcaseLayout>(null);
  const tradeOperationRef =
    useRef<import("../components/TradeOperation").TradeOperationRef>(null);

  // Ref to track if layout has been changed (allows only one change)
  const hasChangedLayoutRef = useRef(false);

  // State and ref to track initialization state
  const [isLayoutMounted, setIsLayoutMounted] = useState(false);
  const hasTriggeredToggleRef = useRef(false);

  // Refs for stable values that don't need to trigger rerenders
  const baseURLRef = useRef(DEFAULT_BASE_URL);
  const wsURLRef = useRef(DEFAULT_WS_URL);

  // Store previous layout for efficient comparison (avoid JSON.stringify)
  const prevLayoutRef = useRef<LayoutItem[]>([]);

  // Configuration - use refs to avoid unnecessary rerenders
  const baseURL = baseURLRef.current;
  const wsURL = wsURLRef.current;

  // Generate initial layout based on selected layout type
  const initialLayout = useMemo(() => {
    const layoutConfig =
      layoutType === "default"
        ? defaultPredefinedLayouts
        : layoutType === "widthfit"
          ? widthfit
          : layoutType === "rightTrade"
            ? rightTrade
            : layoutType === "testTrade"
              ? testTrade
              : triPanel;
    return generateLayout(layoutConfig, 0);
  }, [layoutType]);

  // Initialize layout state - compute initial value inline
  const [layout, setLayout] = useState<LayoutItem[]>(() => {
    const layoutConfig = defaultPredefinedLayouts;
    return generateLayout(layoutConfig, 0);
  });

  // Sync layout state when initialLayout changes (e.g., when layoutType changes)
  useEffect(() => {
    setLayout(initialLayout);
    prevLayoutRef.current = initialLayout;
    // Reset mount state when layout type changes (component will remount)
    setIsLayoutMounted(false);
    hasTriggeredToggleRef.current = false;
  }, [initialLayout]);

  // Load authToken from localStorage (shared with other test pages)
  const [sessionToken, setSessionToken] = useState(() => {
    return localStorage.getItem("authToken") || "";
  });

  // Use markets hook to manage market data retention
  // With autoFetchMarkets: true, markets are automatically fetched on mount
  // The store (useMarkets.ts) automatically selects the first active market (or first market)
  // when markets are loaded, so selectedMarketId will be set automatically
  const {
    markets,
    selectedMarketId,
    selectedMarketSymbol,
    setSelectedMarketId,
    loadingMarkets,
    getError,
  } = useMarkets({
    baseURL,
    sessionToken,
    autoFetchMarkets: true, // Auto-fetch markets on mount and auto-select first market
  });
  const { client: subscriptionClient, toggleEnabled } = useSubscription();
  // Use onboarding hook as single source of truth for account information
  const {
    mnemonic,
    walletAddress,
    authToken: onboardAuthToken,
    setMnemonic: setOnboardMnemonic,
  } = useOnboard(baseURL);

  // Initialize mnemonic with default value if not set
  useEffect(() => {
    if (!mnemonic) {
      setOnboardMnemonic(DEFAULT_MNEMONIC);
    }
  }, [mnemonic, setOnboardMnemonic]);

  // Update prevLayoutRef when layout changes from external sources
  useEffect(() => {
    prevLayoutRef.current = layout;
  }, [layout]);

  // Sync onboard auth token to sessionToken when it changes
  useEffect(() => {
    if (onboardAuthToken) {
      setSessionToken(onboardAuthToken);
      localStorage.setItem("authToken", onboardAuthToken);
    }
  }, [onboardAuthToken]);

  // Sync authToken and walletAddress to SubscriptionProvider context
  const { setAuthToken, setWalletAddress } = useSubscription();
  useEffect(() => {
    setAuthToken(sessionToken || undefined);
  }, [sessionToken, setAuthToken]);
  useEffect(() => {
    setWalletAddress(walletAddress || undefined);
  }, [walletAddress, setWalletAddress]);

  // Sync sessionToken to localStorage whenever it changes (except when syncing from localStorage)
  // Use ref to track if we're syncing from localStorage to prevent loops
  const isSyncingFromStorageRef = useRef(false);
  useEffect(() => {
    if (
      sessionToken &&
      sessionToken.trim() !== "" &&
      !isSyncingFromStorageRef.current
    ) {
      const stored = localStorage.getItem("authToken");
      if (stored !== sessionToken) {
        localStorage.setItem("authToken", sessionToken);
      }
    }
    isSyncingFromStorageRef.current = false;
  }, [sessionToken]);

  // Listen for authToken changes from localStorage (when other pages/tabs update it)
  // Optimized: increased interval and use ref to prevent unnecessary updates
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "authToken" && e.newValue) {
        isSyncingFromStorageRef.current = true;
        setSessionToken(e.newValue);
      }
    };

    window.addEventListener("storage", handleStorageChange);

    // Check localStorage periodically - increased interval to reduce checks (5s instead of 1s)
    const interval = setInterval(() => {
      const storedAuthToken = localStorage.getItem("authToken");
      // Use functional update to avoid dependency on sessionToken
      setSessionToken((currentToken) => {
        if (storedAuthToken && storedAuthToken !== currentToken) {
          isSyncingFromStorageRef.current = true;
          return storedAuthToken;
        }
        return currentToken;
      });
    }, 5000); // Reduced frequency: 5 seconds instead of 1

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, []); // Empty deps - only run once on mount

  // Market ID selection flow:
  // 1. Component mounts with autoFetchMarkets: true
  // 2. useMarkets hook automatically calls fetchMarkets() when markets array is empty
  // 3. fetchMarkets() loads markets from API and calls setMarkets() in the store
  // 4. setMarkets() in marketStore.ts automatically selects the first active market
  //    (or first market) if selectedMarketId is null
  // 5. Component re-renders with selectedMarketId set, ready to use
  // No additional useEffect needed - the store handles auto-selection automatically

  // Callback when layout is mounted
  const handleLayoutMount = useCallback(() => {
    setIsLayoutMounted(true);
  }, []);

  // Wait for all layouts to be initialized, then trigger toggleEnabled
  useEffect(() => {
    // Check if everything is ready:
    // 1. Layout is mounted
    // 2. Markets are loaded (not loading and have markets)
    // 3. A market is selected
    // 4. We haven't already triggered toggleEnabled
    const isReady =
      isLayoutMounted &&
      !loadingMarkets &&
      markets.length > 0 &&
      selectedMarketId &&
      !hasTriggeredToggleRef.current;

    if (isReady) {
      hasTriggeredToggleRef.current = true;
      // Use setTimeout to ensure all components are fully rendered
      setTimeout(() => {
        toggleEnabled();
      }, 100);
    }
  }, [
    isLayoutMounted,
    loadingMarkets,
    markets.length,
    selectedMarketId,
    toggleEnabled,
  ]);

  // Compute default market ID (first active market, or first in list)
  // Use market_id (string) for the store (selectedMarketId is string)
  // Memoize to prevent recalculation on every render
  const defaultMarketId = useMemo(() => {
    return markets.length > 0
      ? (markets.find((m) => m.active)?.market_id ??
          markets[0].market_id ??
          markets.find((m) => m.active)?.id?.toString() ??
          markets[0].id?.toString() ??
          null)
      : null;
  }, [markets]);

  // Optimized layout change handler with efficient comparison and one-time change limit
  // Only processes changes when not in static mode and hasn't changed yet
  const onLayoutChange = useCallback(
    (newLayout: Layout[], _layouts: ResponsiveLayouts) => {
      // If in static mode, ignore layout changes to prevent unnecessary updates
      if (allStatic) {
        return;
      }

      // Efficient comparison without JSON.stringify - compare item by item
      const prevLayout = prevLayoutRef.current;
      const newLayoutItems = newLayout as LayoutItem[];

      // Quick length check
      if (prevLayout.length !== newLayoutItems.length) {
        hasChangedLayoutRef.current = true;
        prevLayoutRef.current = newLayoutItems;
        setLayout(newLayoutItems);
        return;
      }

      // Efficient item-by-item comparison (much faster than JSON.stringify)
      let hasChanged = false;
      for (let i = 0; i < prevLayout.length; i++) {
        const prev = prevLayout[i] as any;
        const next = newLayoutItems[i] as any;
        if (
          prev.x !== next.x ||
          prev.y !== next.y ||
          prev.w !== next.w ||
          prev.h !== next.h ||
          prev.i !== next.i
        ) {
          hasChanged = true;
          break;
        }
      }

      // Only update if layout actually changed
      if (hasChanged) {
        hasChangedLayoutRef.current = true;
        prevLayoutRef.current = newLayoutItems;
        setLayout(newLayoutItems);
      }
    },
    [allStatic],
  ); // Only recreate when allStatic changes

  const handleCompactTypeChange = useCallback(() => {
    setCompactType((prev) =>
      prev === "horizontal"
        ? "vertical"
        : prev === "vertical"
          ? null
          : "horizontal",
    );
  }, []);

  const handleToggleFullWidth = useCallback(() => {
    setFullWidth((prev) => !prev);
  }, []);

  // Apply fullWidth class to body when fullWidth state changes
  useEffect(() => {
    if (fullWidth) {
      document.body.classList.add("full-width-screen");
    } else {
      document.body.classList.remove("full-width-screen");
    }
    // Cleanup on unmount
    return () => {
      document.body.classList.remove("full-width-screen");
    };
  }, [fullWidth]);

  const handleToggleStatic = useCallback(() => {
    setAllStatic((prev) => !prev);
    // Reset layout change flag when toggling static mode
    // This allows one more layout change if user unlocks
    if (allStatic) {
      hasChangedLayoutRef.current = false;
    }
  }, [allStatic]);

  const handleNewLayout = useCallback(() => {
    if (showcaseLayoutRef.current) {
      // Reset the layout change flag when generating new layout
      hasChangedLayoutRef.current = false;
      showcaseLayoutRef.current.generateNewLayout();
    }
  }, []);

  const handleCopyLayoutMatrix = useCallback(async () => {
    try {
      // Sort layout items by slot index (i) and format as [x, y, w, h] arrays
      const layoutMatrix = layout
        .slice()
        .sort((a, b) => {
          const aLayout = a as any;
          const bLayout = b as any;
          const aIndex = parseInt(aLayout.i, 10);
          const bIndex = parseInt(bLayout.i, 10);
          return aIndex - bIndex;
        })
        .map((item) => {
          const itemLayout = item as any;
          return `  [${itemLayout.x}, ${itemLayout.y}, ${itemLayout.w}, ${itemLayout.h}]`;
        })
        .join(",\n");

      const formattedOutput = `[\n${layoutMatrix},\n]`;
      await navigator.clipboard.writeText(formattedOutput);
      alert("Layout matrix copied to clipboard!");
    } catch (err) {
      console.error("Failed to copy layout matrix:", err);
      alert("Failed to copy layout matrix to clipboard");
    }
  }, [layout]);

  // Memoize stringifyLayout to prevent recalculation on every render
  const stringifyLayout = useMemo(() => {
    return layout.map(function (l) {
      const layoutItem = l as any;
      return (
        <div className="layoutItem" key={layoutItem.i}>
          <b>{layoutItem.i}</b>: [{layoutItem.x}, {layoutItem.y}, {layoutItem.w}
          , {layoutItem.h}]
        </div>
      );
    });
  }, [layout]);

  // Ensure selectedMarketId is a string (type guard) - compute before early returns
  // This allows hooks below to use it safely even if markets aren't loaded yet
  const currentMarketId: string = selectedMarketId || "";

  // Calculate dimensions for each slot based on layout
  // Using useMemo for derived state to prevent unnecessary recalculations and loops
  // This is recalculated only when layout changes, avoiding state update cycles
  // MUST be called before any early returns to follow Rules of Hooks
  // Grid configuration constants (stable, no need to memoize)
  const ROW_HEIGHT = 30;
  const MARGIN_Y = 10;
  const COLS = 12;

  const slotDimensions = useMemo(() => {
    if (layout.length === 0) {
      return {};
    }

    const dimensions: {
      [slotIndex: number]: { width: string; height: string };
    } = {};

    // Calculate dimensions for slots 0-6 (7 slots total)
    for (let slotIndex = 0; slotIndex <= 6; slotIndex++) {
      const layoutItem = layout.find((item) => {
        const itemLayout = item as any;
        return itemLayout.i === slotIndex.toString();
      });

      if (layoutItem) {
        const itemLayout = layoutItem as any;
        // Calculate pixel height: h * rowHeight + (h - 1) * verticalMargin
        const pixelHeight =
          itemLayout.h * ROW_HEIGHT + (itemLayout.h - 1) * MARGIN_Y;

        // Calculate width as percentage for responsiveness
        const widthPercent = (itemLayout.w / COLS) * 100;

        dimensions[slotIndex] = {
          width: `${widthPercent}%`,
          height: `${pixelHeight}px`,
        };
      }
    }

    return dimensions;
  }, [layout]);

  // Handle price click from orderbook to set price in TradeOperation
  const handleOrderbookPriceClick = useCallback((price: number) => {
    if (tradeOperationRef.current) {
      tradeOperationRef.current.setPrice(price);
    }
  }, []);

  // Use currentMarketId in the key to force re-render when market changes
  // Memoize to prevent unnecessary re-creation of components
  // Dimensions are dynamically calculated from layout and passed to components that support them
  // MUST be called before any early returns to follow Rules of Hooks
  // Optimized: Split into separate memoized components to reduce dependencies
  const demoComponents: React.ReactNode[] = useMemo(
    () => [
      // Slot 0: TradingViewWidget - stable, no dependencies
      <TradingViewWidget
        key="tv"
        symbol="BINANCE:BTCUSDT"
        interval="5"
        isCollapsed={false}
      />,
      // Slot 1: Orderbook002 - uses dynamically calculated height
      <Orderbook002
        key={`clobx-${currentMarketId}`}
        baseURL={baseURL}
        subscriptionClient={subscriptionClient}
        marketId={currentMarketId}
        maxDepth={20}
        height={slotDimensions[1]?.height}
        onPriceClick={handleOrderbookPriceClick}
      />,
      // Slot 2: TradePrice - symbol is derived from marketId via useMarkets
      <TradePrice
        key={`price-${currentMarketId}`}
        baseURL={baseURL}
        subscriptionClient={subscriptionClient}
        marketId={currentMarketId}
      />,
      // Slot 3: TradeOperation
      <TradeOperation
        ref={tradeOperationRef}
        key={`trade-${currentMarketId}`}
        baseURL={baseURL}
        mnemonic={mnemonic}
        marketId={currentMarketId}
        selectedBucketBalance={selectedBucketBalance}
        selectedBucketId={selectedBucketId}
        authToken={sessionToken}
        walletAddress={walletAddress}
        onLeverageClick={() => {
          if (tradeOperationRef.current) {
            setTempLeverage(tradeOperationRef.current.leverage);
          }
          setIsLeverageModalOpen(true);
        }}
        onNumberCalculatorClick={() => {
          if (tradeOperationRef.current) {
            const currentSize = tradeOperationRef.current.getSizeInput();
            const maxSize = tradeOperationRef.current.getAvailableTradeAmount();
            setNumberCalculatorValue(currentSize);
            setNumberCalculatorMax(maxSize);
            setIsNumberCalculatorOpen(true);
          }
        }}
        // marketSymbol is now auto-derived from selected market via useMarkets
      />,
      // Slot 4: PendingOrders - uses dynamically calculated height
      <PendingOrders
        key={`order-${currentMarketId}`}
        baseURL={baseURL}
        subscriptionClient={subscriptionClient}
        authToken={sessionToken}
        walletAddress={walletAddress}
        mnemonic={mnemonic}
        height={slotDimensions[4]?.height}
        refreshInterval={5000}
      />,
      // Slot 5: BucketPositions - uses dynamically calculated height
      <BucketPositions
        key={`pos-${currentMarketId}`}
        baseURL={baseURL}
        subscriptionClient={subscriptionClient}
        authToken={sessionToken}
        walletAddress={walletAddress}
        mnemonic={mnemonic}
        height={slotDimensions[5]?.height}
        refreshInterval={5000}
        selectedBucketId={selectedBucketId}
      />,
      // Slot 6: BucketCollections - uses dynamically calculated height
      <BucketCollections
        key={`bucket-${currentMarketId}`}
        baseURL={baseURL}
        subscriptionClient={subscriptionClient}
        mnemonic={mnemonic}
        authToken={sessionToken}
        walletAddress={walletAddress}
        height={slotDimensions[6]?.height}
        refreshInterval={5000}
        onBucketSelect={(bucketId, nominalValue) => {
          setSelectedBucketId(bucketId);
          setSelectedBucketBalance(nominalValue);
        }}
      />,
      // Slot 7: Orderbook001 - uses dynamically calculated height
      <DummyComponent
        key={`clobclassic-${currentMarketId}`}
        title="Dummy Component"
        message="This is a dummy component"
        height={slotDimensions[7]?.height}
      />,
      // Slot 8: TickerBar - uses dynamically calculated height
      <TickerBar
        key={`ticker-${currentMarketId}`}
        baseURL={baseURL}
        selectedMarketId={currentMarketId}
        height={slotDimensions[8]?.height}
      />,
    ],
    [
      baseURL,
      wsURL,
      sessionToken,
      walletAddress,
      currentMarketId,
      selectedMarketSymbol, // Include symbol in dependencies
      slotDimensions,
      selectedBucketId,
      selectedBucketBalance,
      handleOrderbookPriceClick,
    ],
  );

  // Show loading state while markets are being fetched
  // Early returns MUST come AFTER all hooks to follow Rules of Hooks
  if (loadingMarkets) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <div>Loading markets...</div>
      </div>
    );
  }

  // Show error state if markets failed to load
  const marketsError = getError("markets");
  if (marketsError) {
    return (
      <div style={{ padding: "2rem", textAlign: "center", color: "#ff6b6b" }}>
        <div>Error loading markets: {marketsError}</div>
      </div>
    );
  }

  // Show message if no markets available or no market selected
  if (markets.length === 0 || !selectedMarketId) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <div>No markets available.</div>
      </div>
    );
  }

  return (
    <div className="App-content-row">
      {/* Configuration Section */}
      <div className="App-section">
        <h3 style={{ marginTop: "0", marginBottom: "0.75rem" }}>
          Configuration
        </h3>
        <div
          style={{
            display: "flex",
            gap: "1rem",
            alignItems: "flex-end",
            flexWrap: "wrap",
          }}
        >
          <div style={{ flex: "1", minWidth: "300px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "0.25rem",
                fontWeight: "500",
              }}
            >
              Market:
            </label>
            <select
              value={selectedMarketId || ""}
              onChange={(e) => {
                const val = e.target.value;
                if (val) {
                  // Use string market_id directly for the store
                  setSelectedMarketId(val);
                }
              }}
              style={{
                width: "100%",
                padding: "0.5rem",
                fontSize: "1rem",
                borderRadius: "4px",
                border: "1px solid #ccc",
                backgroundColor: "white",
                color: "#333",
              }}
            >
              {markets.length === 0 ? (
                <option value="">Loading markets...</option>
              ) : (
                <>
                  <option value="" disabled>
                    -- Select a market --
                  </option>
                  {markets.map((market) => {
                    // Use market_id (string) for the store (selectedMarketId is string)
                    // Fallback to id converted to string if market_id doesn't exist
                    const marketIdValue =
                      market.market_id ?? market.id?.toString() ?? "";
                    // Prioritize moniker (from API), then symbol, then fallback
                    const displayName = market.moniker
                      ? market.moniker
                      : market.symbol
                        ? market.symbol
                        : market.baseAsset && market.quoteAsset
                          ? `${market.baseAsset}-${market.quoteAsset}`
                          : `Market ID: ${marketIdValue}`;
                    const isActive = market.active ? " [ACTIVE]" : "";

                    return (
                      <option key={marketIdValue} value={marketIdValue}>
                        {displayName} - ID: {marketIdValue}
                        {isActive}
                      </option>
                    );
                  })}
                </>
              )}
            </select>
            {markets.length > 0 && (
              <small
                style={{
                  display: "block",
                  marginTop: "0.25rem",
                  color:
                    selectedMarketId === defaultMarketId
                      ? "#51cf66"
                      : "#ff9f43",
                }}
              >
                {selectedMarketId === defaultMarketId
                  ? `✓ Auto-selected default market (ID: ${defaultMarketId?.substring(0, 16)}...)`
                  : `⚠ Manual selection (ID: ${selectedMarketId?.substring(0, 16)}...)`}
              </small>
            )}
          </div>
          <div style={{ flex: "1", minWidth: "300px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "0.25rem",
                fontWeight: "500",
              }}
            >
              Mnemonic (for wallet address):
            </label>
            <input
              type="text"
              value={mnemonic || ""}
              onChange={(e) => setOnboardMnemonic(e.target.value)}
              placeholder="Enter mnemonic to generate wallet address"
              style={{
                width: "100%",
                padding: "0.5rem",
                fontSize: "1rem",
                border: "1px solid #ccc",
                backgroundColor: "white",
                color: "#333",
              }}
            />
          </div>
          <div style={{ flex: "1", minWidth: "300px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "0.25rem",
                fontWeight: "500",
              }}
            >
              Wallet Address:
            </label>
            <input
              type="text"
              value={walletAddress || ""}
              disabled={!!(mnemonic && mnemonic.trim() !== "")}
              placeholder="automatic (from mnemonic via useOnboard)"
              style={{
                width: "100%",
                padding: "0.5rem",
                fontSize: "1rem",
                border: "1px solid #ccc",
                backgroundColor: walletAddress ? "#e7f3ff" : "#f5f5f5",
                color: "#333",
              }}
            />
            {walletAddress && (
              <small
                style={{
                  display: "block",
                  marginTop: "0.25rem",
                  color: "#28a745",
                }}
              >
                ✓ Auto-generated from mnemonic via useOnboard hook
              </small>
            )}
          </div>
          <div style={{ flex: "1", minWidth: "300px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "0.25rem",
                fontWeight: "500",
              }}
            >
              Layout Type:
            </label>
            <select
              value={layoutType}
              onChange={(e) => {
                const val = e.target.value as
                  | "default"
                  | "widthfit"
                  | "rightTrade"
                  | "testTrade"
                  | "triPanel";
                setLayoutType(val);
              }}
              style={{
                width: "100%",
                padding: "0.5rem",
                fontSize: "1rem",
                borderRadius: "4px",
                border: "1px solid #ccc",
                backgroundColor: "white",
                color: "#333",
              }}
            >
              <option value="default">Default Predefined Layouts</option>
              <option value="widthfit">Width Fit</option>
              <option value="rightTrade">Right Trade</option>
              <option value="testTrade">Test Trade</option>
              <option value="triPanel">Tri Panel</option>
            </select>
          </div>
        </div>
        <h3>Layout Controls</h3>
        <button onClick={handleNewLayout} style={{ marginRight: "0.5rem" }}>
          Generate New Layout
        </button>
        <button onClick={handleToggleStatic} style={{ marginRight: "0.5rem" }}>
          {allStatic ? "Enable Dragging" : "Disable Dragging"}
        </button>
        <button
          onClick={handleCompactTypeChange}
          style={{ marginRight: "0.5rem" }}
        >
          Change Compaction Type
        </button>
        <button onClick={handleToggleFullWidth}>Full Width Screen</button>
        <div style={{ marginTop: "0.5rem", fontSize: "0.9em", color: "#fff" }}>
          Compaction type:{" "}
          {compactType
            ? compactType.charAt(0).toUpperCase() + compactType.slice(1)
            : "No Compaction"}
        </div>
      </div>

      <ShowcaseLayout
        key={layoutType}
        ref={showcaseLayoutRef}
        onLayoutChange={onLayoutChange}
        slotContent={demoComponents}
        compactType={compactType}
        allStatic={allStatic}
        onCompactTypeChange={handleCompactTypeChange}
        onToggleStatic={handleToggleStatic}
        onNewLayout={handleNewLayout}
        initialLayout={initialLayout}
        onMount={handleLayoutMount}
      />
      <div className="overview-layout layoutJSON">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "0.5rem",
          }}
        >
          <span>
            Displayed as <code>[x, y, w, h]</code>:
          </span>
          <button
            onClick={handleCopyLayoutMatrix}
            style={{
              padding: "0.5rem 1rem",
              fontSize: "0.9rem",
              borderRadius: "4px",
              border: "1px solid #313131",
              backgroundColor: "#ccc",
              color: "black",
              cursor: "pointer",
              transition: "background-color 0.2s",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.color = "white";
              e.currentTarget.style.backgroundColor = "#0056b3";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.color = "black";
              e.currentTarget.style.backgroundColor = "#ccc";
            }}
          >
            Copy
          </button>
        </div>
        <div className="columns">{stringifyLayout}</div>
      </div>
      {/* Leverage Selection Modal */}
      <LeverageModal
        isOpen={isLeverageModalOpen}
        currentLeverage={tradeOperationRef.current?.leverage || 10}
        tempLeverage={tempLeverage}
        maxRatio={
          tradeOperationRef.current
            ? tradeOperationRef.current.calculateMaxRatio(tempLeverage)
            : tempLeverage.toFixed(2)
        }
        onLeverageChange={setTempLeverage}
        onConfirm={async () => {
          // Check if bucket is selected before proceeding
          if (
            !selectedBucketId ||
            selectedBucketId === "--" ||
            selectedBucketId.trim() === ""
          ) {
            // Don't close modal, show error and return early
            toast.error("Bucket Required", {
              description: "Please select a bucket before changing leverage.",
            });
            return;
          }

          if (tradeOperationRef.current) {
            // Close modal immediately for better UX
            setIsLeverageModalOpen(false);
            // Call changeLeverage to make the API call
            // The API call will show loading toast and handle success/error via TradeOperation's useEffect
            try {
              await tradeOperationRef.current.changeLeverage(tempLeverage, {
                bucketId: selectedBucketId,
              });
            } catch (error) {
              // Error is already handled by TradeOperation's error handling
              // Reset tempLeverage to current leverage on error
              if (tradeOperationRef.current) {
                setTempLeverage(tradeOperationRef.current.leverage);
              }
            }
          } else {
            setIsLeverageModalOpen(false);
          }
        }}
        onCancel={() => {
          if (tradeOperationRef.current) {
            setTempLeverage(tradeOperationRef.current.leverage);
          }
          setIsLeverageModalOpen(false);
        }}
      />
      {/* Number Calculator Modal */}
      <NumberPad
        isOpen={isNumberCalculatorOpen}
        value={numberCalculatorValue}
        min={0}
        max={numberCalculatorMax}
        onConfirm={(value) => {
          if (tradeOperationRef.current) {
            tradeOperationRef.current.setSizeInput(value);
          }
          setIsNumberCalculatorOpen(false);
        }}
        onCancel={() => {
          setIsNumberCalculatorOpen(false);
        }}
      />
    </div>
  );
}

/**
 * Wrapper component that provides SubscriptionContext
 * Required because child components (TradePrice, TradeOperation, PendingOrders, etc.)
 * use the useSubscription hook which requires SubscriptionProvider
 */
export default function LayoutTestPage() {
  return (
    <SubscriptionProvider defaultWsURL={DEFAULT_WS_URL}>
      <TestLayoutExchange />
    </SubscriptionProvider>
  );
}
